import React, { Suspense } from 'react';
import ReactDOM from 'react-dom/client';
import './silenceProductionLogs'; // must run before any modules that log on import
import './chunkLoadRecovery';
import { clearChunkRetryMarkers } from './lazyWithRetry';
import './api/apiTransportDiagnostics';
import './api/setupApiDebugLogging'; // interceptors are guarded by NODE_ENV inside the file
import './index.css';
import reportWebVitals from './reportWebVitals';
// react-multi-carousel CSS is now imported only inside ProductDetails (the
// only consumer) so it loads on demand instead of in main.css for every page.
import { RouterProvider } from 'react-router-dom';
import { router } from './routes';
import { Provider } from 'react-redux';
import store from './store/store';
import { HelmetProvider } from 'react-helmet-async';
import AuthStorageSync from './components/AuthStorageSync';
import { BUILD_STAMP } from './api/constant';

// If we're here, the current bundle's main chunk loaded successfully.
// Wipe any stale retry markers from a previous reload so the next deploy
// gets a fresh budget of one auto-retry per route chunk. Must run AFTER
// all imports for the `import/first` eslint rule.
clearChunkRetryMarkers();

// Admin-only deferred-upload helpers — never used on storefront pages, so we
// load them only when the URL starts with /admin. Saves ~40KB from the main
// bundle for every customer.
const UploadQueueManager = React.lazy(() => import('./components/UploadQueueManager'));
const UploadQueueStatusBadge = React.lazy(() => import('./components/UploadQueueStatusBadge'));
const isAdminRoute = () =>
  typeof window !== 'undefined' && String(window.location?.pathname || '').startsWith('/admin');

// After deploy, confirm this line in DevTools → you are not on a cached shell from an older build.
console.info('[pinksisly] UI build stamp:', BUILD_STAMP);

const rootElement = document.getElementById('root');

const App = (
  <HelmetProvider>
    <Provider store={store}>
      <AuthStorageSync />
      {isAdminRoute() && (
        <Suspense fallback={null}>
          <UploadQueueManager />
          <UploadQueueStatusBadge />
        </Suspense>
      )}
      <RouterProvider router={router} />
    </Provider>
  </HelmetProvider>
);

// If react-snap pre-rendered HTML exists in the root, hydrate instead of replacing.
// Empty roots (dev / first request before snap runs) fall back to createRoot + render.
if (rootElement && rootElement.hasChildNodes()) {
  ReactDOM.hydrateRoot(rootElement, App);
} else {
  ReactDOM.createRoot(rootElement).render(App);
}

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();

// PWA (Phase H): cache the app shell + static assets so the dashboard and
// /staff keep working on flaky store wifi. Production only — a SW in dev
// serves stale bundles and fights the dev server.
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker
      .register('/service-worker.js')
      .catch((e) => console.warn('[PWA] service worker registration failed', e));
  });
}
